import FingerprintJS from '@fingerprintjs/fingerprintjs';

type DeviceNavigator = Navigator & {
  deviceMemory?: number;
};

type UsageMetrics = {
  initTime: number;
  initCount: number;
};

const APP_INIT_TIME_KEY = 'app_init_time';
const APP_INIT_COUNT_KEY = 'app_init_count';
let cachedUsageMetrics: UsageMetrics | null = null;

function getShortestScreenSide() {
  return Math.min(window.screen.width, window.screen.height);
}

function getDeviceMemory() {
  return (navigator as DeviceNavigator).deviceMemory;
}

function readUsageMetrics(): UsageMetrics | null {
  try {
    const storedInitTime = Number(localStorage.getItem(APP_INIT_TIME_KEY));
    const storedInitCount = Number(localStorage.getItem(APP_INIT_COUNT_KEY));

    if (
      Number.isFinite(storedInitTime) &&
      storedInitTime > 0 &&
      Number.isFinite(storedInitCount) &&
      storedInitCount > 0
    ) {
      return {
        initTime: storedInitTime,
        initCount: storedInitCount,
      };
    }

    return null;
  } catch (error) {
    console.warn('读取使用数据缓存失败', error);
    return null;
  }
}

export function initAppUsageMetrics() {
  const existingMetrics = readUsageMetrics();
  const nextMetrics = existingMetrics
    ? {
        initTime: existingMetrics.initTime,
        initCount: existingMetrics.initCount + 1,
      }
    : {
        initTime: Date.now(),
        initCount: 1,
      };

  cachedUsageMetrics = nextMetrics;
  try {
    localStorage.setItem(APP_INIT_TIME_KEY, String(nextMetrics.initTime));
    localStorage.setItem(APP_INIT_COUNT_KEY, String(nextMetrics.initCount));
  } catch (error) {
    console.warn('写入使用数据缓存失败', error);
  }
  return nextMetrics;
}

function getUsageMetrics(): UsageMetrics {
  if (cachedUsageMetrics) {
    return cachedUsageMetrics;
  }

  const storedMetrics = readUsageMetrics();
  if (storedMetrics) {
    cachedUsageMetrics = storedMetrics;
    return storedMetrics;
  }

  return {
    initTime: Date.now(),
    initCount: 1,
  };
}

function createFallbackDeviceId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `pc-${globalThis.crypto.randomUUID()}`;
  }

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `pc-${hex}`;
  }

  return `pc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildAppInfoPayload() {
  const { initTime, initCount } = getUsageMetrics();

  return {
    platform: 'pc',
    channel: 'web',
    init_time: initTime,
    init_count: initCount,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? undefined,
    shortest: getShortestScreenSide(),
    device_pixel_ratio: window.devicePixelRatio || 1,
    device_memory: getDeviceMemory(),
  };
}

/**
 * 获取APP信息
 * @returns {string} JSON string of app info
 */
export function getSerializedAppInfo() {
  return JSON.stringify(buildAppInfoPayload());
}

/**
 * 获取设备唯一凭证
 * @returns {Promise<string>}
 */
export async function getDeviceIdentity(): Promise<string> {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  } catch (error) {
    console.warn('获取设备指纹失败，降级使用随机ID', error);
    // 简单模拟设备ID，实际项目中可能需要持久化存储或使用更复杂的生成逻辑
    let deviceId = localStorage.getItem('device_identity');
    if (!deviceId) {
      deviceId = createFallbackDeviceId();
      localStorage.setItem('device_identity', deviceId);
    }
    return deviceId;
  }
}

export function getDefaultRequestHeaders() {
  return {
    channel: 'web',
    'app-info': getSerializedAppInfo(),
  };
}

export function getHostUrl() {
  return import.meta.env.VITE_BASE_URL || window.location.origin;
}

export function getOfficialSiteUrl() {
  return import.meta.env.VITE_OFFICIAL_SITE_URL || window.location.origin;
}

export function openOfficialSite() {
  window.open(getOfficialSiteUrl(), '_blank', 'noopener,noreferrer');
}
